import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  downloadTemplateEquipamentos,
  useEquipamentoImportar,
  type ImportResultado,
} from '../../hooks/useEquipamentos'
import { useClientesV2 } from '../../hooks/useEmpresas'
import styles from '../_shared/CrudPage.module.css'

const ACAO_LABEL: Record<string, { label: string; bg: string; color: string }> = {
  criar: { label: 'Criar', bg: '#E8F5E9', color: '#2E7D32' },
  ignorar_duplicado: { label: 'Ignorar (duplicado)', bg: '#FFF8E1', color: '#F9A825' },
  erro: { label: 'Erro', bg: '#FFEBEE', color: '#C62828' },
}

export default function EquipamentoImportarPage() {
  const navigate = useNavigate()
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [clienteId, setClienteId] = useState<string>('')
  const [preview, setPreview] = useState<ImportResultado | null>(null)
  const clientes = useClientesV2()
  const importar = useEquipamentoImportar()

  function extractError(err: unknown): string {
    const e = err as { response?: { status?: number; data?: unknown }; message?: string }

    function primeiraMensagem(valor: unknown): string | null {
      if (typeof valor === 'string' && valor.trim()) {
        const texto = valor.trim()
        if (texto.startsWith('<!') || texto.startsWith('<html')) return null
        return texto
      }
      if (Array.isArray(valor)) {
        for (const item of valor) {
          const mensagem = primeiraMensagem(item)
          if (mensagem) return mensagem
        }
      }
      if (valor && typeof valor === 'object') {
        const objeto = valor as Record<string, unknown>
        for (const chave of ['arquivo', 'cliente_id', 'detail', 'message', 'non_field_errors']) {
          const mensagem = primeiraMensagem(objeto[chave])
          if (mensagem) return mensagem
        }
        for (const item of Object.values(objeto)) {
          const mensagem = primeiraMensagem(item)
          if (mensagem) return mensagem
        }
      }
      return null
    }

    return (
      (e.response?.status === 404
        ? 'O recurso de importação ainda não está disponível neste servidor.'
        : null) ??
      primeiraMensagem(e.response?.data) ??
      e.message ??
      'Não foi possível processar a planilha.'
    )
  }

  function extractDownloadError(err: unknown): string {
    const e = err as {
      response?: { status?: number; data?: unknown }
      code?: string
      message?: string
    }
    const status = e?.response?.status
    if (status === 404) {
      return 'Endpoint do template não está disponível neste backend. Atualize o backend ou contate o suporte.'
    }
    if (status === 401 || status === 403) {
      return 'Sem permissão para baixar o template. Faça login novamente.'
    }
    if (e?.code === 'ERR_NETWORK' || e?.message === 'Network Error') {
      return 'Sem conexão com o servidor. Tente novamente.'
    }
    if (status && status >= 500) {
      return `Servidor indisponível (HTTP ${status}). Tente novamente em alguns instantes.`
    }
    return 'Falha ao baixar template — tente novamente.'
  }

  async function handlePreview() {
    if (!arquivo || !clienteId) {
      notifications.show({ color: 'red', message: 'Selecione um arquivo e o cliente de destino.' })
      return
    }
    try {
      const r = await importar.mutateAsync({ arquivo, cliente_id: clienteId, dry_run: true })
      setPreview(r)
    } catch (err) {
      notifications.show({ color: 'red', title: 'Falha no preview', message: extractError(err) })
    }
  }

  async function handleConfirmar() {
    if (!arquivo || !clienteId || !preview) return
    if (preview.a_criar === 0) {
      notifications.show({ color: 'orange', message: 'Nenhuma linha para criar.' })
      return
    }
    if (
      !confirm(
        `Confirmar importação de ${preview.a_criar} equipamento(s)? Esta ação é irreversível.`,
      )
    )
      return
    try {
      const r = await importar.mutateAsync({ arquivo, cliente_id: clienteId, dry_run: false })
      notifications.show({
        color: 'green',
        message: `${r.commit?.criados ?? 0} equipamento(s) criado(s) com sucesso.`,
      })
      navigate('/equipamentos')
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha no commit',
        message: extractError(err),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            onClick={() => navigate('/equipamentos')}
            type="button"
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>Importar planilha de equipamentos</h1>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              await downloadTemplateEquipamentos()
              notifications.show({
                color: 'green',
                message: 'Template baixado.',
              })
            } catch (e) {
              notifications.show({
                color: 'red',
                title: 'Não foi possível baixar o template',
                message: extractDownloadError(e),
              })
            }
          }}
          style={{
            padding: '0.5rem 1rem',
            background: 'white',
            color: '#1565C0',
            border: '1px solid #1565C0',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          📥 Baixar template
        </button>
      </header>

      <section
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>1. Selecione o arquivo</h2>
        <input
          type="file"
          accept=".xlsx,.xls"
          data-testid="import-arquivo"
          onChange={(e) => {
            setArquivo(e.target.files?.[0] ?? null)
            setPreview(null)
          }}
        />
        {arquivo && (
          <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.85rem' }}>
            {arquivo.name} • {(arquivo.size / 1024).toFixed(1)} KB
          </p>
        )}

        <h2 style={{ marginTop: '1.5rem', fontSize: '1rem' }}>2. Cliente de destino</h2>
        <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
          Todos os equipamentos e suas áreas serão cadastrados neste cliente. A coluna FILIAL é
          apenas informativa e divergências serão exibidas no preview.
        </p>
        <select
          data-testid="import-cliente"
          value={clienteId}
          onChange={(e) => setClienteId(e.target.value)}
          disabled={clientes.isLoading}
          style={{ padding: '0.5rem', minWidth: 280 }}
        >
          <option value="">— selecione —</option>
          {clientes.data?.results.map((c) => (
            <option key={c.id} value={c.id}>
              {c.razao_social}
            </option>
          ))}
        </select>

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={handlePreview}
            disabled={!arquivo || !clienteId || importar.isPending}
            data-testid="import-preview"
            style={{
              padding: '0.5rem 1.25rem',
              background: '#1565C0',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {importar.isPending && !preview ? 'Analisando...' : 'Analisar planilha'}
          </button>
        </div>
      </section>

      {preview && (
        <section
          style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: 8,
            marginTop: '1rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: '1rem' }}>3. Preview</h2>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              padding: '1rem',
              background: '#FAFAFA',
              borderRadius: 4,
              marginBottom: '1rem',
            }}
          >
            <div data-testid="preview-total">
              <strong>{preview.total_linhas}</strong> linhas lidas
            </div>
            <div data-testid="preview-criar" style={{ color: '#2E7D32' }}>
              <strong>{preview.a_criar}</strong> a criar
            </div>
            <div data-testid="preview-duplicados" style={{ color: '#F9A825' }}>
              <strong>{preview.duplicados}</strong> duplicados
            </div>
            <div data-testid="preview-erros" style={{ color: '#C62828' }}>
              <strong>{preview.erros}</strong> erros
            </div>
          </div>

          {preview.mapeamento_colunas && preview.mapeamento_colunas.length > 0 && (
            <details
              style={{ marginBottom: '1rem', padding: '0.75rem', background: '#FAFAFA', borderRadius: 4 }}
            >
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Mapa de colunas (
                {preview.mapeamento_colunas.filter((c) => c.reconhecida).length}/
                {preview.mapeamento_colunas.length} reconhecidas)
              </summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                <thead>
                  <tr style={{ background: '#F0F0F0' }}>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Header da planilha</th>
                    <th style={{ padding: '0.4rem', textAlign: 'left' }}>Campo do sistema</th>
                    <th style={{ padding: '0.4rem' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.mapeamento_colunas.map((c) => (
                    <tr key={c.coluna} style={{ borderTop: '1px solid #EEE' }}>
                      <td style={{ padding: '0.4rem' }}>{c.header_planilha}</td>
                      <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>
                        {c.campo_sistema ?? <em style={{ color: '#999' }}>(ignorada)</em>}
                      </td>
                      <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                        {c.reconhecida ? '✅' : '⚠️'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {Object.keys(preview.sites_resolvidos).length > 0 && (
            <div
              style={{
                padding: '0.75rem',
                background: '#FFF8E1',
                borderRadius: 4,
                marginBottom: '1rem',
                fontSize: '0.85rem',
              }}
            >
              <strong>Avisos de filial:</strong>
              <ul style={{ margin: '0.25rem 0 0 1rem' }}>
                {Object.values(preview.sites_resolvidos).map((aviso, i) => (
                  <li key={i}>{aviso}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.avisos?.length > 0 && (
            <details
              style={{
                padding: '0.75rem',
                background: '#FFF8E1',
                borderRadius: 4,
                marginBottom: '1rem',
                fontSize: '0.85rem',
              }}
            >
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                {preview.avisos.length} aviso(s) de dados opcionais
              </summary>
              <ul style={{ margin: '0.5rem 0 0 1rem' }}>
                {preview.avisos.map((aviso, i) => (
                  <li key={i}>{aviso}</li>
                ))}
              </ul>
            </details>
          )}

          <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #E0E0E0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ background: '#F5F5F5', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Linha</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Ação</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Resumo</th>
                </tr>
              </thead>
              <tbody>
                {preview.linhas.map((l) => {
                  const meta = ACAO_LABEL[l.acao]
                  const d = l.dados as Record<string, unknown>
                  const ehErro = l.acao === 'erro'
                  const resumo = ehErro
                    ? (l.erro ?? '(sem detalhe)')
                    : l.acao === 'criar'
                      ? `${d.tipo ?? ''} • ${d.marca ?? ''} ${d.modelo ?? ''} • ${d.capacidade_btu ?? '—'} BTU`
                      : (l.erro ?? '')
                  return (
                    <tr
                      key={l.linha}
                      style={{
                        borderTop: '1px solid #EEE',
                        background: ehErro ? '#FFEBEE' : undefined,
                      }}
                      data-testid={`linha-${l.linha}`}
                    >
                      <td style={{ padding: '0.5rem', fontWeight: ehErro ? 600 : 400 }}>
                        {l.linha}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        <span
                          style={{
                            background: meta.bg,
                            color: meta.color,
                            padding: '0.15rem 0.5rem',
                            borderRadius: 999,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '0.5rem',
                          color: ehErro ? '#C62828' : undefined,
                          fontWeight: ehErro ? 500 : 400,
                        }}
                      >
                        {resumo}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={handleConfirmar}
              disabled={preview.a_criar === 0 || importar.isPending}
              data-testid="import-confirmar"
              style={{
                padding: '0.5rem 1.25rem',
                background: '#2E7D32',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {importar.isPending ? 'Importando...' : `✓ Confirmar (${preview.a_criar})`}
            </button>
            <button
              type="button"
              onClick={() => setPreview(null)}
              style={{
                padding: '0.5rem 1.25rem',
                background: 'white',
                color: '#666',
                border: '1px solid #888',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
